import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getLeadDetail } from "@/lib/admin.functions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PhoneCell } from "@/routes/_authenticated/admin.leads";

export const Route = createFileRoute("/_authenticated/admin/leads_/$leadId")({
  head: () => ({
    meta: [
      { title: "Lead-tijdlijn | Beheer IT Solutions" },
      { name: "description", content: "Volledige tijdlijn van een lead." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Lead-tijdlijn | Beheer IT Solutions" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: LeadDetailPage,
});

const STATUS_LABEL: Record<string, string> = {
  new: "Nieuw",
  contacted: "Gecontacteerd",
  quoted: "Offerte verstuurd",
  won: "Gewonnen",
  lost: "Verloren",
};

const MAIL_KIND_LABEL: Record<string, string> = {
  download: "Downloadlink",
  download_opened: "Opvolging na openen link",
  followup: "Opvolgmail 48u",
  confirmation: "Bevestigingsmail",
  internal: "Interne notificatie",
  session: "Beoordelingssessie",
};

function fmt(iso: string) {
  return new Date(iso).toLocaleString("nl-BE", { dateStyle: "medium", timeStyle: "short" });
}

type TimelineItem = {
  at: string;
  label: string;
  detail?: string;
  tone: "brand" | "ok" | "err" | "muted";
};

function LeadDetailPage() {
  const { leadId } = Route.useParams();
  const detailFn = useServerFn(getLeadDetail);
  const q = useQuery({
    queryKey: ["lead-detail", leadId],
    queryFn: () => detailFn({ data: { id: leadId } }),
    retry: false,
  });

  if (q.isLoading) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-10">
        <p className="text-muted-foreground">Laden…</p>
      </main>
    );
  }
  if (q.isError || !q.data) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-10">
        <p className="text-destructive">
          {/* i18n-ignore */}Kon deze lead niet laden (geen toegang of niet gevonden).
        </p>
        <BackLink />
      </main>
    );
  }

  const { lead, events, emails, downloads } = q.data;
  const items: TimelineItem[] = [];

  items.push({
    at: lead.created_at,
    label: "Aanvraag ontvangen",
    detail: [
      `type ${lead.form_type}`,
      lead.package_name,
      [lead.utm_source, lead.utm_medium, lead.utm_campaign].filter(Boolean).join(" · ") || null,
      lead.referrer ? `via ${lead.referrer}` : null,
    ]
      .filter(Boolean)
      .join(" — "),
    tone: "brand",
  });

  for (const e of events) {
    if (e.kind === "status_change") {
      const from = STATUS_LABEL[String(e.data.from)] ?? String(e.data.from ?? "?");
      const to = STATUS_LABEL[String(e.data.to)] ?? String(e.data.to ?? "?");
      items.push({ at: e.created_at, label: `Status: ${from} → ${to}`, tone: "muted" });
    } else {
      items.push({ at: e.created_at, label: e.kind, tone: "muted" });
    }
  }

  for (const m of emails) {
    items.push({
      at: m.created_at,
      label: `${m.sent ? "E-mail verstuurd" : "E-mail mislukt"}: ${MAIL_KIND_LABEL[m.kind] ?? m.kind}`,
      detail: m.error ? `${m.subject} — fout: ${m.error}` : m.subject,
      tone: m.sent ? "ok" : "err",
    });
  }

  for (const d of downloads) {
    items.push({ at: d.created_at, label: "Downloadlink aangemaakt", tone: "muted" });
    if (d.first_opened_at) {
      items.push({
        at: d.first_opened_at,
        label: "Link geopend",
        detail: `${d.open_count}× bekeken`,
        tone: "brand",
      });
    }
    if (d.first_downloaded_at) {
      items.push({
        at: d.first_downloaded_at,
        label: "Rapport gedownload",
        detail: `${d.download_count}/${d.max_downloads} downloads`,
        tone: "ok",
      });
    }
  }

  items.sort((a, b) => a.at.localeCompare(b.at));

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-10 md:px-8">
      <BackLink />

      <div className="mb-8 mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
            {lead.first_name} {lead.last_name}
          </h1>
          <p className="text-sm text-muted-foreground">
            {lead.email}
            {lead.company ? ` · ${lead.company}` : ""}
          </p>
          <p className="mt-1 flex items-center gap-3 text-sm">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Telefoon</span>
            <PhoneCell value={lead.gsm} />
            {lead.gsm ? (
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(lead.gsm)}
                className="text-xs text-muted-foreground underline-offset-4 hover:underline"
              >
                Kopieer
              </button>
            ) : null}
          </p>
        </div>
        <Badge variant="secondary">{STATUS_LABEL[lead.status] ?? lead.status}</Badge>
      </div>

      <dl className="mb-10 grid gap-3 rounded-xl border border-border bg-card p-5 text-sm sm:grid-cols-2">
        <Field
          label="Adres"
          value={[lead.street, lead.number, lead.postcode, lead.city, lead.country]
            .filter(Boolean)
            .join(" ")}
        />
        <Field label="Website" value={lead.website} />
        <Field label="Taal" value={lead.lang.toUpperCase()} />
        <Field label="Pakket" value={lead.package_name ?? ""} />
        <Field label="Offertenummer" value={lead.quote_number ?? ""} />
        <Field label="Toestemming" value={lead.consent_at ? fmt(lead.consent_at) : ""} />
      </dl>

      {lead.message ? (
        <div className="mb-10">
          <h2 className="text-sm font-medium">Bericht</h2>
          <p className="mt-1 whitespace-pre-wrap rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">
            {lead.message}
          </p>
        </div>
      ) : null}

      <h2 className="mb-4 text-lg font-medium">Tijdlijn</h2>
      <ol className="relative space-y-0 border-l-2 border-border pl-6">
        {items.map((it, i) => (
          <li key={i} className="relative pb-6 last:pb-0">
            <span
              className={`absolute -left-[31px] top-1 h-3 w-3 rounded-full border-2 border-background ${
                it.tone === "err"
                  ? "bg-destructive"
                  : it.tone === "ok"
                    ? "bg-emerald-500"
                    : it.tone === "brand"
                      ? "bg-primary"
                      : "bg-muted-foreground/50"
              }`}
            />
            <div className="text-sm font-medium">{it.label}</div>
            <div className="text-xs text-muted-foreground">{fmt(it.at)}</div>
            {it.detail ? (
              <div className="mt-0.5 text-xs text-muted-foreground">{it.detail}</div>
            ) : null}
          </li>
        ))}
      </ol>
    </main>
  );
}

function BackLink() {
  return (
    <Button variant="ghost" size="sm" asChild>
      <Link to="/admin/leads">{/* i18n-ignore */}← Terug naar leads</Link>
    </Button>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="break-words">{value || "—"}</dd>
    </div>
  );
}
